<script setup>
import { computed } from 'vue'
import { useI18n } from '../i18n.js'
import {
  ArrowPathIcon,
  ChevronRightIcon,
  CpuChipIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/24/outline'
import Pts2DeviceIcon from '../components/pts2/Pts2DeviceIcon.vue'
import Pts2DiagnosticsStrip from '../components/pts2/Pts2DiagnosticsStrip.vue'
import { PTS2_CATEGORIES, controllerCount } from '../pts2/controllers.js'
import {
  configurationCaption,
  controllerConnection,
  controllerTime,
  useControllerPages,
} from '../pts2/useControllerPages.js'
import '../pts2/controllers.css'
const { locale, t } = useI18n()
const categories = computed(() => PTS2_CATEGORIES.map((item) => ({ ...item, label: t(`pts2UiCategory_${item.key}`) })))
const {
  filters,
  visibleControllers,
  summaryCursor,
  diagnostics,
  loading,
  refreshing,
  switching,
  error,
  refresh,
  openCurrent,
} = useControllerPages()
</script>

<template>
  <section class="pc-page" aria-labelledby="controllers-title">
    <header class="pc-heading">
      <div>
        <h1 id="controllers-title">{{ t('pts2UiControllers') }}</h1>
        <p>{{ t('pts2UiNetwork') }}</p>
      </div>
    </header>
    <div class="pc-toolbar">
      <label class="pc-search"
        ><MagnifyingGlassIcon aria-hidden="true" /><input
          v-model="filters.search"
          maxlength="120"
          :aria-label="t('pts2UiControllerSearch')"
          :placeholder="t('pts2UiControllerSearchPlaceholder')"
          type="search"
      /></label>
      <select v-model="filters.state" :aria-label="t('pts2UiHealth')">
        <option value="">{{ t('pts2UiAllStates') }}</option>
        <option value="FRESH">{{ t('pts2UiFreshness_fresh') }}</option>
        <option value="STALE">{{ t('pts2UiFreshness_stale') }}</option>
        <option value="OFFLINE">{{ t('pts2UiFreshness_offline') }}</option>
        <option value="UNKNOWN">{{ t('pts2UiFreshness_unknown') }}</option>
      </select>
      <button class="pc-refresh" :disabled="refreshing" @click="refresh()">
        <ArrowPathIcon :class="{ spinning: refreshing }" aria-hidden="true" />{{ t('pts2UiRefresh') }}
      </button>
    </div>
    <p v-if="error" class="pc-notice" role="alert">{{ error }} <button @click="refresh()">{{ t('pts2UiRetry') }}</button></p>
    <p class="pc-result-count" role="status">
      {{ loading ? t('pts2UiLoadingControllers') : t('pts2UiControllerCount', { count: visibleControllers.length })
      }}<span v-if="summaryCursor">
        · {{ t('pts2UiMoreControllersAvailable') }}</span
      >
    </p>
    <div v-if="loading" class="pc-skeleton-grid" aria-hidden="true">
      <div v-for="n in 3" :key="n" class="pc-skeleton"></div>
    </div>
    <div v-else-if="!visibleControllers.length && !error" class="pc-empty">
      <CpuChipIcon aria-hidden="true" />
      <h2>{{ t(filters.search || filters.state ? 'pts2UiNoMatchingControllers' : 'pts2UiNoControllers') }}</h2>
      <p>
        {{
          filters.search || filters.state
            ? t('pts2UiControllerFilterHelp')
            : t('pts2UiNetworkControllerHelp')
        }}
      </p>
      <button
        v-if="filters.search || filters.state"
        @click="Object.assign(filters, { search: '', state: '' })"
      >
        {{ t('pts2UiResetFilters') }}
      </button>
    </div>
    <div v-else class="pc-card-grid">
      <article v-for="item in visibleControllers" :key="item.id" class="pc-card">
        <header class="pc-card-heading">
          <CpuChipIcon aria-hidden="true" />
          <div>
            <h2>{{ item.stationDisplayName }}</h2>
            <p>{{ item.stableCode }} · {{ item.displayName }}</p>
          </div>
        </header>
        <div class="pc-card-connection">
          <span class="pc-state" :class="error ? 'unknown' : controllerConnection(item.connectionFreshness, t).tone"
            ><i></i>{{ error ? t('pts2UiFreshnessUnconfirmed') : controllerConnection(item.connectionFreshness, t).label }}</span
          ><small
            >{{ t('pts2UiLastData') }}<br /><time>{{ controllerTime(item.statusReceivedAt, locale) }}</time></small
          >
        </div>
        <h3>{{ t('pts2UiDeviceInventory') }}</h3>
        <dl class="pc-device-counts">
          <div v-for="category in categories" :key="category.key">
            <dt><Pts2DeviceIcon :category="category.key" />{{ category.label }}</dt>
            <dd
              :title="
                item.counts?.[category.key]?.completeness === 'PARTIAL'
                  ? t('pts2UiIncompleteConfiguration', { count: item.counts[category.key].observedCount })
                  : undefined
              "
            >
              {{ controllerCount(item, category.key) ?? '—' }}
            </dd>
          </div>
        </dl>
        <Pts2DiagnosticsStrip :snapshot="diagnostics[item.id]" :summary="item" :failed="!!error" compact />
        <p class="pc-configuration">{{ configurationCaption(item, t) }}</p>
        <div class="pc-card-actions">
          <RouterLink
            class="pc-primary"
            :to="{ name: 'pts2-controller-detail', params: { controllerId: item.id } }"
            :aria-label="t('pts2UiDetailAccessible', { station: item.stationDisplayName, controller: item.displayName })"
            >{{ t('pts2UiDetails') }} <ChevronRightIcon aria-hidden="true" /></RouterLink
          ><button class="pc-secondary" :disabled="switching || !!error" @click="openCurrent(item.id)">
            {{ t('pts2UiCurrent') }}
          </button>
        </div>
      </article>
    </div>
    <button
      v-if="summaryCursor && !loading"
      class="pc-load-more"
      :disabled="refreshing"
      @click="refresh({ more: true })"
    >
      {{ t('pts2UiLoadMoreControllers') }}
    </button>
  </section>
</template>
